/**
 * The flattened, frozen registry of plugin contributions, provided by
 * WorkbenchPluginHost and read by the primitives that host each seam (the board
 * edge overlay, the details panel, the board chrome). Kept separate from the
 * host component so hooks/context live in a `.ts` (Fast-Refresh clean).
 *
 * Default value is the shared EMPTY registry, so a workbench mounted without a
 * host (the viewer) reads empty frozen arrays and renders exactly as before.
 */
import { createContext, useContext } from "react";
import type {
  BoardLayerContribution,
  CommandContribution,
  DetailSectionContribution,
  DetailSelection,
  HeaderItemContribution,
  StatusItemContribution,
  NavSectionContribution,
  PanelKind,
  MenuItemContribution,
  ContextMenuContribution,
  SettingsPageContribution,
} from "./types.js";

/** A contribution tagged with its owning plugin id — the id keys the error
 *  boundary each contribution renders inside, so one plugin's crash is isolated. */
export interface Owned<T> {
  readonly pluginId: string;
  readonly contribution: T;
}

export interface PluginRegistry {
  readonly boardLayers: readonly Owned<BoardLayerContribution>[];
  readonly detailSections: readonly Owned<DetailSectionContribution>[];
  readonly headerItems: readonly Owned<HeaderItemContribution>[];
  readonly statusItems: readonly Owned<StatusItemContribution>[];
  readonly navSections: readonly Owned<NavSectionContribution>[];
  /** Custom-tab kinds, keyed by `panelKind.kind` for O(1) resolve. First
   *  registration of a kind wins (a later duplicate is dropped with a warn). */
  readonly panelKinds: ReadonlyMap<string, Owned<PanelKind>>;
  readonly commands: readonly Owned<CommandContribution>[];
  /** App-menu items (sorted by order), read out-of-host via the registry bridge. */
  readonly menuItems: readonly Owned<MenuItemContribution>[];
  /** Right-click menu extenders, filtered by target at the render site. */
  readonly contextMenu: readonly Owned<ContextMenuContribution>[];
  /** Settings pages (sorted by order), read out-of-host via the registry bridge. */
  readonly settingsPages: readonly Owned<SettingsPageContribution>[];
}

const EMPTY: readonly never[] = Object.freeze([]);
const EMPTY_PANEL_KINDS: ReadonlyMap<string, Owned<PanelKind>> = Object.freeze(
  new Map<string, Owned<PanelKind>>(),
);

export const EMPTY_REGISTRY: PluginRegistry = Object.freeze({
  boardLayers: EMPTY as readonly Owned<BoardLayerContribution>[],
  detailSections: EMPTY as readonly Owned<DetailSectionContribution>[],
  headerItems: EMPTY as readonly Owned<HeaderItemContribution>[],
  statusItems: EMPTY as readonly Owned<StatusItemContribution>[],
  navSections: EMPTY as readonly Owned<NavSectionContribution>[],
  panelKinds: EMPTY_PANEL_KINDS,
  commands: EMPTY as readonly Owned<CommandContribution>[],
  menuItems: EMPTY as readonly Owned<MenuItemContribution>[],
  contextMenu: EMPTY as readonly Owned<ContextMenuContribution>[],
  settingsPages: EMPTY as readonly Owned<SettingsPageContribution>[],
});

const Ctx = createContext<PluginRegistry>(EMPTY_REGISTRY);
export const PluginRegistryProvider = Ctx.Provider;

export function usePluginRegistry(): PluginRegistry {
  return useContext(Ctx);
}

/** Board overlay layers to render inside each space's edge SVG (stable frozen
 *  array — safe to read in the hot board render path). */
export function useBoardLayerContributions(): readonly Owned<BoardLayerContribution>[] {
  return usePluginRegistry().boardLayers;
}

/** Board-chrome header items to merge into `chromeTrailingItems`. */
export function useHeaderItemContributions(): readonly Owned<HeaderItemContribution>[] {
  return usePluginRegistry().headerItems;
}

/** Status-bar items, unfiltered by `when` (the StatusBar primitive gates each
 *  against the live host ctx at render time). */
export function useStatusItemContributions(): readonly Owned<StatusItemContribution>[] {
  return usePluginRegistry().statusItems;
}

/** Left-rail nav sections, unfiltered by `when` (the nav wiring gates each
 *  against the live host ctx when building the tab strip). */
export function useNavSectionContributions(): readonly Owned<NavSectionContribution>[] {
  return usePluginRegistry().navSections;
}

/** A stable resolver `kind → PanelKind | undefined` over the registered custom
 *  tab kinds. The store is handed this so its tab switches can delegate the
 *  custom arm; an unregistered kind resolves to undefined (→ dropped on reload). */
export function usePanelKindResolver(): (kind: string) => PanelKind | undefined {
  const map = usePluginRegistry().panelKinds;
  return (kind) => map.get(kind)?.contribution;
}

/** Command contributions, unfiltered by `when` (the CommandPaletteHost gates each
 *  against the live host ctx at palette-open). */
export function useCommandContributions(): readonly Owned<CommandContribution>[] {
  return usePluginRegistry().commands;
}

/** All right-click menu extenders (stable frozen array — safe in the board
 *  render path). Read IN-host by BoardPane and other menu-building surfaces,
 *  which filter by target + gate by `when` at menu-open time, appending the
 *  resolved rows after their built-in items. */
export function useContextMenuContributions(): readonly Owned<ContextMenuContribution>[] {
  return usePluginRegistry().contextMenu;
}

/** Detail sections applicable to the current selection. Called by DetailsPane
 *  (which then threads the resolved sections down as a prop, keeping the view
 *  components pure) — NOT read inside a pure Sidebar view. */
export function resolveDetailSections(
  registry: PluginRegistry,
  selection: DetailSelection,
): readonly Owned<DetailSectionContribution>[] {
  return registry.detailSections.filter((o) => o.contribution.appliesTo(selection));
}
